import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listServices,
  createService,
  updateService,
  deleteService,
  type CreateServiceInput,
  type UpdateServiceInput,
} from '../lib/servicesApi';

// Services nomenclature. The shared `['services']` key feeds the Settings tab
// and every service dropdown (tasks, projects, people, financial tagging).
// Mutations also invalidate tasks/projects/people: all embed the service
// name/color.

export const SERVICES_KEY = ['services'] as const;

export function useServices(includeInactive = false) {
  return useQuery({
    queryKey: [...SERVICES_KEY, { includeInactive }],
    queryFn: () => listServices(includeInactive),
    staleTime: 5 * 60 * 1000,
  });
}

function useInvalidateServices() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: SERVICES_KEY });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['people'] });
  };
}

export function useCreateService() {
  const invalidate = useInvalidateServices();
  return useMutation({
    mutationFn: (input: CreateServiceInput) => createService(input),
    onSuccess: invalidate,
  });
}

export function useUpdateService() {
  const invalidate = useInvalidateServices();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateServiceInput }) =>
      updateService(id, input),
    onSuccess: invalidate,
  });
}

export function useDeleteService() {
  const invalidate = useInvalidateServices();
  return useMutation({
    mutationFn: (id: string) => deleteService(id),
    onSuccess: invalidate,
  });
}
